import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { BACKEND_URL } from '../utils/config';
import {
  AlertCircle,
  CheckCircle,
  Copy,
  Send,
  Star,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Filter,
  Download,
  Loader
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const API = `${BACKEND_URL}/api`;

const ReviewsManager = () => {
  const [criticalReviews, setCriticalReviews] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [editedResponse, setEditedResponse] = useState('');
  
  // Scraping states
  const [scrapingStatus, setScrapingStatus] = useState(null);
  const [scrapingSessionId, setScrapingSessionId] = useState(null);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [scrapingInProgress, setScrapingInProgress] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    propertyId: '',
    year: new Date().getFullYear(),
    month: '',
    quarter: '',
    region: '',
    city: '',
    responsible: '',
    platform: '',
    includeAddressed: false
  });

  useEffect(() => {
    fetchProperties();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Build query params
      const params = new URLSearchParams();
      if (filters.propertyId) params.append('property_id', filters.propertyId);
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.quarter) params.append('quarter', filters.quarter);
      if (filters.region) params.append('region', filters.region);
      if (filters.city) params.append('city', filters.city);
      if (filters.responsible) params.append('responsible_email', filters.responsible);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.includeAddressed) params.append('include_addressed', 'true');

      // Fetch critical reviews with filters
      const criticalRes = await axios.get(`${API}/reviews/critical?${params.toString()}`, { headers });
      setCriticalReviews(criticalRes.data);

      // Fetch stats
      const statsParams = new URLSearchParams();
      if (filters.year) statsParams.append('year', filters.year);
      if (filters.propertyId) statsParams.append('property_id', filters.propertyId);
      
      const statsRes = await axios.get(`${API}/reviews/stats?${statsParams.toString()}`, { headers });
      setStats(statsRes.data);

    } catch (error) {
      console.error('Error fetching reviews:', error);
      alert('Errore nel caricamento delle recensioni');
    } finally {
      setLoading(false);
    }
  };

  const startScrapingWithLogin = async () => {
    try {
      setScrapingInProgress(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(
        `${API}/reviews/start-scraping-with-login?platform=booking`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const sessionId = response.data.session_id;
        setScrapingSessionId(sessionId);
        
        // Start polling for status
        pollScrapingStatus(sessionId);
      }
    } catch (error) {
      console.error('Error starting scraping:', error);
      alert(error.response?.data?.detail || 'Errore durante l\'avvio dello scraping');
      setScrapingInProgress(false);
    }
  };

  const pollScrapingStatus = async (sessionId) => {
    const token = localStorage.getItem('hw_token');
    
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/reviews/scraping-status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const status = response.data;
        setScrapingStatus(status);
        
        // Check if OTP is needed
        if (status.needs_otp && !showOtpDialog) {
          setShowOtpDialog(true);
        }
        
        // Check if completed or failed
        if (status.status === 'completed' || status.status === 'error' || status.status === 'failed') {
          clearInterval(interval);
          setScrapingInProgress(false);
          
          // Refresh reviews after completion
          if (status.status === 'completed') {
            setTimeout(fetchData, 2000);
          }
        }
      } catch (error) {
        console.error('Error polling status:', error);
        clearInterval(interval);
        setScrapingInProgress(false);
      }
    }, 3000); // Poll every 3 seconds
  };

  const submitOtp = async () => {
    try {
      if (!otpCode || !scrapingSessionId) return;
      
      const token = localStorage.getItem('hw_token');
      await axios.post(
        `${API}/reviews/submit-otp/${scrapingSessionId}?otp_code=${otpCode}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowOtpDialog(false);
      setOtpCode('');
    } catch (error) {
      console.error('Error submitting OTP:', error);
      alert('Errore nell\'invio del codice OTP');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Risposta copiata negli appunti!');
  };

  const handleMarkAddressed = async (reviewId, finalResponse) => {
    try {
      const token = localStorage.getItem('hw_token');
      await axios.post(`${API}/reviews/${reviewId}/mark-addressed`, 
        { final_response: finalResponse },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      // Save feedback if response was edited
      if (selectedReview && editedResponse !== selectedReview.suggested_response) {
        await axios.post(`${API}/reviews/response/save`,
          {
            review_id: reviewId,
            suggested_response: selectedReview.suggested_response,
            modified_response: editedResponse
          },
          { headers: { Authorization: `Bearer ${token}` }}
        );
      }

      alert('Recensione segnata come gestita!');
      setSelectedReview(null);
      setEditedResponse('');
      fetchData();
    } catch (error) {
      console.error('Error marking review as addressed:', error);
      alert('Errore nel salvare la risposta');
    }
  };

  const getProblemBadge = (problemType) => {
    const config = {
      property_issue: { color: 'bg-red-100 text-red-800', label: '🏠 Problema Casa' },
      management_issue: { color: 'bg-orange-100 text-orange-800', label: '👤 Problema Gestione' },
      mixed: { color: 'bg-yellow-100 text-yellow-800', label: '⚠️ Misto' },
      positive: { color: 'bg-green-100 text-green-800', label: '✅ Positiva' }
    };
    const cfg = config[problemType] || config.positive;
    return <Badge className={cfg.color}>{cfg.label}</Badge>;
  };

  const getPlatformBadge = (platform) => {
    return platform === 'booking' ? 
      <Badge className="bg-blue-100 text-blue-800">📘 Booking.com</Badge> :
      <Badge className="bg-pink-100 text-pink-800">🏠 Airbnb</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  // Prepare chart data
  const monthlyData = stats?.monthly_stats ? Object.entries(stats.monthly_stats).map(([month, data]) => ({
    month: month.substring(5), // Get MM from YYYY-MM
    rating: data.avg_rating.toFixed(2),
    count: data.count,
    critical: data.critical_count
  })) : [];

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestione Recensioni</h1>
            <p className="text-gray-600">Monitora e rispondi alle recensioni critiche</p>
          </div>
          <div className="flex gap-2">
            {scrapingInProgress ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg">
                <Loader className="h-4 w-4 animate-spin" />
                <span className="text-sm font-medium">
                  {scrapingStatus?.message || 'Scraping in corso...'}
                </span>
                {scrapingStatus?.progress > 0 && (
                  <span className="text-xs">({scrapingStatus.progress}%)</span>
                )}
              </div>
            ) : (
              <Button 
                onClick={startScrapingWithLogin} 
                className="gap-2 bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
              >
                <Download className="h-4 w-4" />
                Estrai Recensioni Booking.com
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5" />
            <h3 className="text-lg font-bold">Filtri</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Property Filter with Search */}
            <div className="relative">
              <label className="text-sm font-medium mb-1 block">Immobile</label>
              <input
                type="text"
                placeholder="Cerca immobile per nome..."
                value={
                  filters.propertyId 
                    ? allProperties.find(p => p.id === filters.propertyId)?.name || '' 
                    : ''
                }
                onChange={(e) => {
                  const searchTerm = e.target.value.toLowerCase();
                  if (!searchTerm) {
                    setFilters({...filters, propertyId: ''});
                  } else {
                    const matchedProperty = allProperties.find(p => 
                      p.name.toLowerCase().includes(searchTerm)
                    );
                    if (matchedProperty) {
                      setFilters({...filters, propertyId: matchedProperty.id});
                    }
                  }
                }}
                list="properties-datalist"
                className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
              />
              <datalist id="properties-datalist">
                {allProperties.map(prop => (
                  <option key={prop.id} value={prop.name} />
                ))}
              </datalist>
              {filters.propertyId && (
                <button
                  onClick={() => setFilters({...filters, propertyId: ''})}
                  className="absolute right-2 top-8 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Year Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Anno</label>
              <input
                type="number"
                value={filters.year}
                onChange={(e) => setFilters({...filters, year: e.target.value, month: '', quarter: ''})}
                className="w-full p-2 border rounded"
                min="2020"
                max="2030"
              />
            </div>

            {/* Month Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Mese</label>
              <select
                value={filters.month}
                onChange={(e) => setFilters({...filters, month: e.target.value, quarter: ''})}
                className="w-full p-2 border rounded"
                disabled={!filters.year}
              >
                <option value="">Tutti i mesi</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i+1} value={i+1}>
                    {new Date(2000, i).toLocaleDateString('it-IT', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>

            {/* Quarter Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Trimestre</label>
              <select
                value={filters.quarter}
                onChange={(e) => setFilters({...filters, quarter: e.target.value, month: ''})}
                className="w-full p-2 border rounded"
                disabled={!filters.year}
              >
                <option value="">Tutti i trimestri</option>
                <option value="1">Q1 (Gen-Mar)</option>
                <option value="2">Q2 (Apr-Giu)</option>
                <option value="3">Q3 (Lug-Set)</option>
                <option value="4">Q4 (Ott-Dic)</option>
              </select>
            </div>

            {/* Region Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Regione</label>
              <input
                type="text"
                value={filters.region}
                onChange={(e) => setFilters({...filters, region: e.target.value})}
                placeholder="es: Liguria"
                className="w-full p-2 border rounded"
              />
            </div>

            {/* City Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Città</label>
              <input
                type="text"
                value={filters.city}
                onChange={(e) => setFilters({...filters, city: e.target.value})}
                placeholder="es: Genova"
                className="w-full p-2 border rounded"
              />
            </div>

            {/* Responsible Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Responsabile</label>
              <input
                type="email"
                value={filters.responsible}
                onChange={(e) => setFilters({...filters, responsible: e.target.value})}
                placeholder="email@example.com"
                className="w-full p-2 border rounded"
              />
            </div>

            {/* Platform Filter */}
            <div>
              <label className="text-sm font-medium mb-1 block">Piattaforma</label>
              <select
                value={filters.platform}
                onChange={(e) => setFilters({...filters, platform: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="">Tutte</option>
                <option value="booking">Booking.com</option>
                <option value="airbnb">Airbnb</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.includeAddressed}
                onChange={(e) => setFilters({...filters, includeAddressed: e.target.checked})}
                className="w-4 h-4"
              />
              <span className="text-sm">Includi recensioni già gestite</span>
            </label>

            <Button
              onClick={() => setFilters({
                propertyId: '',
                year: new Date().getFullYear(),
                month: '',
                quarter: '',
                region: '',
                city: '',
                responsible: '',
                platform: '',
                includeAddressed: false
              })}
              variant="outline"
              size="sm"
            >
              Reset Filtri
            </Button>
          </div>
        </Card>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Totale Recensioni</p>
                  <p className="text-2xl font-bold">{stats.total_reviews}</p>
                </div>
                <Star className="h-8 w-8 text-blue-600" />
              </div>
            </Card>
            
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Recensioni Critiche</p>
                  <p className="text-2xl font-bold text-red-600">{stats.critical_count}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Media Booking</p>
                  <p className="text-2xl font-bold">{stats.avg_rating_booking.toFixed(1)}/10</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-600" />
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Gestite</p>
                  <p className="text-2xl font-bold text-green-600">{stats.addressed_count}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </Card>
          </div>
        )}

        {/* Charts */}
        {monthlyData.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="text-lg font-bold mb-4">Andamento Rating Mensile</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="rating" stroke="#2563eb" name="Rating Medio" />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6">
              <h3 className="text-lg font-bold mb-4">Recensioni per Mese</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#10b981" name="Totali" />
                  <Bar dataKey="critical" fill="#ef4444" name="Critiche" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}

        {/* Critical Reviews List */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">🚨 Recensioni Critiche da Gestire</h2>
          
          {criticalReviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg">Nessuna recensione critica da gestire!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {criticalReviews.map((review) => (
                <Card key={review.id} className="p-4 border-l-4 border-red-500">
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-lg">{review.property_name || 'Immobile'}</h3>
                          {getPlatformBadge(review.platform)}
                          {getProblemBadge(review.problem_type)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span>👤 {review.guest_name}</span>
                          <span>⭐ {review.rating}/{review.platform === 'booking' ? '10' : '5'}</span>
                          <span>📅 {new Date(review.review_date).toLocaleDateString('it-IT')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Review Text */}
                    {review.review_text && (
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm italic">"{review.review_text}"</p>
                      </div>
                    )}

                    {/* AI Analysis */}
                    {review.ai_analysis && (
                      <div className="bg-blue-50 p-3 rounded">
                        <p className="text-sm font-semibold text-blue-900 mb-1">🤖 Analisi AI:</p>
                        <p className="text-sm text-blue-800">{review.ai_analysis}</p>
                      </div>
                    )}

                    {/* Suggested Response */}
                    {review.suggested_response && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-green-900">💡 Risposta Suggerita:</p>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => copyToClipboard(review.suggested_response)}
                            className="gap-2"
                          >
                            <Copy className="h-3 w-3" />
                            Copia
                          </Button>
                        </div>
                        <Textarea
                          value={selectedReview?.id === review.id ? editedResponse : review.suggested_response}
                          onChange={(e) => {
                            setSelectedReview(review);
                            setEditedResponse(e.target.value);
                          }}
                          rows={4}
                          className="text-sm"
                        />
                        <Button
                          onClick={() => handleMarkAddressed(review.id, selectedReview?.id === review.id ? editedResponse : review.suggested_response)}
                          className="gap-2 w-full"
                        >
                          <Send className="h-4 w-4" />
                          Segna come Gestita
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        {/* OTP Dialog */}
        {showOtpDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md p-6 space-y-4">
              <h2 className="text-xl font-bold text-slate-900">Autenticazione 2FA Richiesta</h2>
              <p className="text-slate-600">
                Booking.com richiede un codice di verifica. Controlla la tua email o app di autenticazione e inserisci il codice qui sotto.
              </p>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Codice OTP
                </label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="123456"
                  maxLength="6"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowOtpDialog(false);
                    setOtpCode('');
                  }}
                >
                  Annulla
                </Button>
                <Button
                  onClick={submitOtp}
                  disabled={!otpCode}
                  className="bg-gradient-to-r from-blue-900 to-blue-800 hover:from-blue-800 hover:to-blue-700"
                >
                  Invia Codice
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReviewsManager;
